import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { AreaFilterService } from '../services/area-filter.service';

@Component({
  selector: 'app-fui-dashboard',
  templateUrl: './fui-dashboard.component.html',
  styleUrls: ['./fui-dashboard.component.scss']
})
export class FuiDashboardComponent implements OnInit {

  loading = false;
  employeeCount: number | null = null;
  latestPayroll: any = null;
  monthlyWage: any = null;

  // Expense dashboard state
  expenses: any[] = [];
  filteredExpenses: any[] = [];
  expenseDateField: string = 'exp_date';
  expenseFrom: any = null;
  expenseTo: any = null;

  constructor(private api: ApiService, private areaFilterService: AreaFilterService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;

    // employee count
    this.api.getEmployees().subscribe(empRes => {
      const data: any = empRes || {};
      this.employeeCount = this.areaFilterService.filterEmployeesByArea(data.employees || []).length;
    });

    // payroll runs
    this.api.getPayrollRuns().subscribe(runRes => {
      const runs = (runRes && runRes.runs) ? runRes.runs : [];
      if (runs.length > 0) {
        // assume latest run last in list
        this.latestPayroll = runs[runs.length - 1];
        const monthYear = this.latestPayroll.monthYear;
        this.api.getMonthlyWageReport(monthYear).subscribe(rep => {
          this.monthlyWage = rep || null;
          this.loading = false;
        }, _ => {
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    }, _ => {
      this.loading = false;
    });

    // expenses for expense dashboard
    this.api.getAllExpenseDetails().subscribe(expRes => {
      const data: any = expRes || {};
      this.expenses = this.areaFilterService.filterByAreaName(data.expenses || data || []);
      this.filteredExpenses = [...this.expenses];
      this.applyExpenseFilters();
    });
  }

  onExpenseDateChange(): void {
    this.applyExpenseFilters();
  }

  private applyExpenseFilters(): void {
    const from = this.expenseFrom ? new Date(this.expenseFrom) : null;
    const to = this.expenseTo ? new Date(this.expenseTo) : null;
    const field = this.expenseDateField;

    this.filteredExpenses = this.expenses.filter((row: any) => {
      if (from || to) {
        const raw = row[field];
        if (!raw) {
          return false;
        }
        const d = new Date(raw);
        if (from && d < from) {
          return false;
        }
        if (to && d > to) {
          return false;
        }
      }
      return true;
    });
  }

  get expenseCount(): number {
    return this.filteredExpenses.length;
  }

  get expenseTotal(): number {
    return this.filteredExpenses.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  }

  get expenseApprovedCount(): number {
    return this.filteredExpenses.filter(r => typeof r.status === 'string' && r.status.includes('APPROVED')).length;
  }

  get expensePendingCount(): number {
    return this.filteredExpenses.filter(r => typeof r.status === 'string' && r.status.includes('PENDING')).length;
  }

  get expenseRejectedCount(): number {
    return this.filteredExpenses.filter(r => typeof r.status === 'string' && r.status.includes('REJECT')).length;
  }

  get requesterBreakdown(): { requestor: string; count: number; amount: number }[] {
    const map: { [key: string]: { count: number; amount: number } } = {};
    this.filteredExpenses.forEach(r => {
      const key = r.requestor_name ? r.requestor_name.toString() : 'Unknown';
      if (!map[key]) {
        map[key] = { count: 0, amount: 0 };
      }
      map[key].count += 1;
      map[key].amount += Number(r.amount) || 0;
    });

    return Object.keys(map).map(k => ({
      requestor: k,
      count: map[k].count,
      amount: map[k].amount
    }));
  }

  get categoryBreakdown(): { category: string; subCategory: string; count: number; amount: number }[] {
    const map: { [key: string]: { category: string; subCategory: string; count: number; amount: number } } = {};

    this.filteredExpenses.forEach(r => {
      const cat = r.category ? r.category.toString() : 'Uncategorised';
      const sub = r.sub_category ? r.sub_category.toString() : 'General';
      const key = cat + '|' + sub;

      if (!map[key]) {
        map[key] = { category: cat, subCategory: sub, count: 0, amount: 0 };
      }

      map[key].count += 1;
      map[key].amount += Number(r.amount) || 0;
    });

    const rows = Object.keys(map).map(k => map[k]);

    // sort by total amount descending for a more professional summary
    rows.sort((a, b) => b.amount - a.amount);

    return rows;
  }

  exportExpensesToExcel(): void {
    const rows: any[] = this.filteredExpenses || [];
    if (!rows.length) {
      return;
    }

    const headers = [
      'Expense ID',
      'Expense Date',
      'Category',
      'Sub Category',
      'Super Sub Category',
      'Amount',
      'Remarks',
      'Status',
      'Requester Name',
      'Request Date',
      'Area Name',
      'Auditor Name',
      'Approver Name',
      'Approved Date'
    ];

    const csvRows = [headers.join(',')];

    rows.forEach(r => {
      const line = [
        r.exp_id,
        r.exp_date,
        r.category,
        r.sub_category,
        r.super_sub_category,
        r.amount,
        (r.remarks || '').toString().replace(/"/g, '""'),
        r.status,
        r.requestor_name,
        r.requested_date,
        r.area_name,
        r.auditor_name,
        r.approver_name,
        r.approved_date
      ].map(v => {
        const s = v == null ? '' : v.toString();
        return '"' + s.replace(/"/g, '""') + '"';
      });

      csvRows.push(line.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'expense_dashboard_export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
